/**
 * @fileoverview WebSocket client for the Personal Quick Reaction Live Broadcast Overlay ("/abcdef/overlays/quick-reaction").
 * Connects to the platform's real-time communication channel as `overlay:quick-reaction` (`?tipo=overlay:quick-reaction`)
 * to receive overlay configuration shifts (mode, layout 1 QR / 2 QRs, visibility, offsetY),
 * new play celebration events (`personalQuickReaction.newPlay`), and room resets.
 * 
 * @module utilities/WS/PersonalQuickReactionOverlayWS
 */

let personalQuickReactionOverlayWebSocket = null;
let isPersonalQuickReactionOverlayConnected = false;
let reconnectionTimeoutIdentifier = null;
let connectionAttemptCounter = 0;
const MAXIMUM_RECONNECTION_ATTEMPTS = 25;
const RECONNECTION_DELAY_MILLISECONDS = 3000;

let activeMessageCallback = null;
let activeStatusCallback = null;

/**
 * Establishes a persistent WebSocket connection for the Live Broadcast Overlay.
 * 
 * @function ConnectPersonalQuickReactionOverlayWS
 * @param {Function} [onMessageReceived=()=>{}] - Callback invoked whenever an event is received.
 * @param {Function} [onStatusChange=()=>{}] - Callback invoked when connection status changes ('connecting', 'connected', 'reconnecting', 'error', 'disconnected').
 */
export const ConnectPersonalQuickReactionOverlayWS = (
    onMessageReceived = () => {},
    onStatusChange = () => {}
) => {
    activeMessageCallback = onMessageReceived;
    activeStatusCallback = onStatusChange;

    if (personalQuickReactionOverlayWebSocket && isPersonalQuickReactionOverlayConnected) {
        onStatusChange('connected');
        RequestOverlayState();
        return;
    }

    const establishConnection = () => {
        if (connectionAttemptCounter >= MAXIMUM_RECONNECTION_ATTEMPTS) {
            if (activeStatusCallback) activeStatusCallback('error');
            return;
        }

        connectionAttemptCounter += 1;
        if (activeStatusCallback) activeStatusCallback('connecting');

        try {
            const rawWssUrl = process.env.REACT_APP_API_URL_WSS || 'wss://localhost:5001/ws';
            const webSocketEndpointUrl = `${rawWssUrl}?tipo=overlay:quick-reaction`;

            personalQuickReactionOverlayWebSocket = new WebSocket(webSocketEndpointUrl);

            personalQuickReactionOverlayWebSocket.onopen = () => {
                isPersonalQuickReactionOverlayConnected = true;
                connectionAttemptCounter = 0;
                if (activeStatusCallback) activeStatusCallback('connected');

                // Request initial overlay snapshot upon successful connection
                RequestOverlayState();
            };

            personalQuickReactionOverlayWebSocket.onclose = () => {
                isPersonalQuickReactionOverlayConnected = false;
                personalQuickReactionOverlayWebSocket = null;
                if (activeStatusCallback) activeStatusCallback('reconnecting');

                clearTimeout(reconnectionTimeoutIdentifier);
                reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
            };

            personalQuickReactionOverlayWebSocket.onerror = () => {
                if (activeStatusCallback) activeStatusCallback('error');
            };

            personalQuickReactionOverlayWebSocket.onmessage = (messageEvent) => {
                try {
                    const parsedPayload = JSON.parse(messageEvent.data);

                    // Relay all events: personalQuickReaction.overlayUpdated,
                    // state, config, personalQuickReaction.newPlay, personalQuickReaction.reset
                    if (typeof activeMessageCallback === 'function') {
                        activeMessageCallback(parsedPayload);
                    }
                } catch (parseError) {
                    // Ignore non-JSON or unformatted packets
                }
            };
        } catch (connectionError) {
            if (activeStatusCallback) activeStatusCallback('error');
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = setTimeout(establishConnection, RECONNECTION_DELAY_MILLISECONDS);
        }
    };

    establishConnection();
};

/**
 * Sends a JSON payload through the active Overlay WebSocket connection.
 * 
 * @function SendPersonalQuickReactionOverlayMessage
 * @param {Object} payloadObject - JSON object to transmit.
 * @returns {boolean} True if successfully sent, false otherwise.
 */
export const SendPersonalQuickReactionOverlayMessage = (payloadObject) => {
    try {
        if (personalQuickReactionOverlayWebSocket && personalQuickReactionOverlayWebSocket.readyState === WebSocket.OPEN) {
            personalQuickReactionOverlayWebSocket.send(JSON.stringify(payloadObject));
            return true;
        }
        return false;
    } catch (sendError) {
        console.warn('Error al transmitir mensaje WebSocket Overlay PersonalQuickReaction:', sendError);
        return false;
    }
};

/**
 * Requests the current synchronized overlay state snapshot from the server.
 * 
 * @function RequestOverlayState
 * @param {string} [roomId=null] - Optional room GUID.
 * @returns {boolean} True if sent successfully.
 */
export const RequestOverlayState = (roomId = null) => {
    return SendPersonalQuickReactionOverlayMessage({
        type: 'personal_quick_reaction_request_overlay_state',
        roomId: roomId || undefined,
        data: { roomId: roomId || undefined }
    });
};

/**
 * Closes and cleans up the Personal Quick Reaction overlay WebSocket connection.
 * 
 * @function DisconnectPersonalQuickReactionOverlayWS
 */
export const DisconnectPersonalQuickReactionOverlayWS = () => {
    try {
        if (reconnectionTimeoutIdentifier) {
            clearTimeout(reconnectionTimeoutIdentifier);
            reconnectionTimeoutIdentifier = null;
        }

        if (personalQuickReactionOverlayWebSocket) {
            personalQuickReactionOverlayWebSocket.onopen = null;
            personalQuickReactionOverlayWebSocket.onclose = null;
            personalQuickReactionOverlayWebSocket.onerror = null;
            personalQuickReactionOverlayWebSocket.onmessage = null;

            if (personalQuickReactionOverlayWebSocket.readyState === WebSocket.OPEN) {
                personalQuickReactionOverlayWebSocket.close();
            } else if (personalQuickReactionOverlayWebSocket.readyState === WebSocket.CONNECTING) {
                const socketToClose = personalQuickReactionOverlayWebSocket;
                socketToClose.onopen = () => {
                    try {
                        socketToClose.close();
                    } catch {
                        // Safe ignore
                    }
                };
            }

            personalQuickReactionOverlayWebSocket = null;
            isPersonalQuickReactionOverlayConnected = false;
            connectionAttemptCounter = 0;
        }
    } catch (teardownError) {
        // Safe teardown
    }
};

// =============================================================================
// ALIASES FOR COMPATIBILITY
// =============================================================================

export const ConnectWebSocketForPersonalQuickReactionOverlay = ConnectPersonalQuickReactionOverlayWS;
export const DisconnectWebSocketForPersonalQuickReactionOverlay = DisconnectPersonalQuickReactionOverlayWS;
export const CloseWebSocket = DisconnectPersonalQuickReactionOverlayWS;

const PersonalQuickReactionOverlayWS = {
    ConnectPersonalQuickReactionOverlayWS,
    DisconnectPersonalQuickReactionOverlayWS,
    SendPersonalQuickReactionOverlayMessage,
    RequestOverlayState,
    ConnectWebSocketForPersonalQuickReactionOverlay,
    DisconnectWebSocketForPersonalQuickReactionOverlay,
    CloseWebSocket
};

export default PersonalQuickReactionOverlayWS;

